/**
 * LocationDetailPanel Component
 *
 * Slide-out panel showing location details when clicking a map marker.
 * Displays recent executions, quick actions, and location info.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  MapPin,
  Smartphone,
  PlayCircle,
  CheckCircle2,
  XCircle,
  Clock,
  ExternalLink,
  Activity,
  Wifi,
  AlertTriangle,
  Building2,
  FlaskConical,
  Home,
  Truck,
  Loader2,
} from 'lucide-react';
import { Card, Badge, Button, Spinner } from '@/components/ui';
import { toDate } from '@/lib/date-utils';
import { isInternalExecution } from '@simcheck/shared-types';
import { db } from '@/config/firebase';
import { useAuth } from '@/contexts/AuthContext';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  doc,
  getDoc,
  Timestamp,
} from 'firebase/firestore';
import { formatDistanceToNow } from 'date-fns';

interface LocationDetails {
  id: string;
  name: string;
  displayName?: string;
  type: 'datacenter' | 'office' | 'lab' | 'home' | 'mobile';
  city: string;
  country?: string;
  status: 'active' | 'maintenance' | 'offline' | 'decommissioned';
  modemCount: number;
  availableModemCount: number;
  coordinates?: { latitude: number; longitude: number };
}

interface RecentExecution {
  id: string;
  flowName: string;
  status: 'completed' | 'failed' | 'running' | 'pending';
  startedAt: Date;
  duration?: number;
}

interface LocationDetailPanelProps {
  locationId: string | null;
  onClose: () => void;
}

const LOCATION_TYPE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  datacenter: Building2,
  office: Building2,
  lab: FlaskConical,
  home: Home,
  mobile: Truck,
};

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  active: { bg: 'bg-status-success-bg', text: 'text-status-success' },
  maintenance: { bg: 'bg-status-warning-bg', text: 'text-status-warning' },
  offline: { bg: 'bg-status-offline-bg', text: 'text-status-offline' },
  decommissioned: { bg: 'bg-muted', text: 'text-muted-foreground' },
};

const EXECUTION_STATUS_CONFIG: Record<string, { icon: React.ComponentType<{ className?: string }>; color: string; animate?: boolean }> = {
  completed: { icon: CheckCircle2, color: 'text-status-success' },
  failed: { icon: XCircle, color: 'text-status-error' },
  running: { icon: Loader2, color: 'text-accent-blue', animate: true },
  pending: { icon: Clock, color: 'text-muted-foreground' },
};

export const LocationDetailPanel: React.FC<LocationDetailPanelProps> = ({
  locationId,
  onClose,
}) => {
  const navigate = useNavigate();
  const { activeOrganization } = useAuth();
  const [location, setLocation] = useState<LocationDetails | null>(null);
  const [recentExecutions, setRecentExecutions] = useState<RecentExecution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadLocationDetails = async () => {
      if (!locationId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Load location details
        const locationDoc = await getDoc(doc(db, 'locations', locationId));
        if (!locationDoc.exists()) {
          setError('Location not found');
          setLoading(false);
          return;
        }

        const locationData = locationDoc.data();
        setLocation({
          id: locationDoc.id,
          name: locationData.name,
          displayName: locationData.displayName,
          type: locationData.type || 'datacenter',
          city: locationData.city || 'Unknown',
          country: locationData.countryName,
          status: locationData.status || 'active',
          modemCount: locationData.modemCount || 0,
          availableModemCount: locationData.availableModemCount || 0,
          coordinates: locationData.coordinates,
        });

        // Load recent executions for this location — the active org's own.
        // Unfiltered, this listed every tenant's runs at a shared location;
        // the executions read rule now refuses such a query outright.
        if (!activeOrganization?.id) {
          setRecentExecutions([]);
          setLoading(false);
          return;
        }
        const executionsQuery = query(
          collection(db, 'executions'),
          where('organizationId', '==', activeOrganization.id),
          where('locationId', '==', locationId),
          orderBy('startedAt', 'desc'),
          // Over-fetched: internal runs are dropped below (#267).
          limit(10)
        );
        const executionsSnapshot = await getDocs(executionsQuery);

        const executions: RecentExecution[] = executionsSnapshot.docs
          .filter((execDoc) => !isInternalExecution(execDoc.data()))
          .slice(0, 5)
          .map((execDoc) => {
          const data = execDoc.data();
          const startedAt = data.startedAt
            ? toDate(data.startedAt) || new Date()
            : new Date();
          const completedAt = data.completedAt
            ? toDate(data.completedAt)
            : null;

          let duration: number | undefined;
          if (completedAt && startedAt) {
            duration = Math.round((completedAt.getTime() - startedAt.getTime()) / 1000);
          }

          return {
            id: execDoc.id,
            flowName: data.flowName || 'Unknown Flow',
            status: data.status || 'pending',
            startedAt,
            duration,
          };
        });

        setRecentExecutions(executions);
        setLoading(false);
      } catch (err) {
        console.error('Error loading location details:', err);
        setError('Failed to load location details');
        setLoading(false);
      }
    };

    loadLocationDetails();
  }, [locationId, activeOrganization?.id]);

  if (!locationId) return null;

  const TypeIcon = location ? LOCATION_TYPE_ICONS[location.type] || Building2 : Building2;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-card border-l border-border shadow-xl z-50 overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold text-foreground">Location Details</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center justify-center py-12 text-status-error">
              <AlertTriangle className="w-12 h-12 mb-2" />
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && location && (
            <div className="space-y-6">
              {/* Location Info */}
              <div>
                <div className="flex items-start gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-muted">
                    <TypeIcon className="w-6 h-6 text-foreground" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-foreground">
                      {location.displayName || location.name}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {location.city}
                      {location.country && `, ${location.country}`}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <Badge
                    className={`${STATUS_COLORS[location.status].bg} ${STATUS_COLORS[location.status].text} capitalize`}
                  >
                    {location.status}
                  </Badge>
                  <Badge className="bg-muted text-muted-foreground capitalize">
                    {location.type}
                  </Badge>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-muted">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Smartphone className="w-4 h-4" />
                    <span className="text-xs">Modems</span>
                  </div>
                  <div className="text-2xl font-semibold text-foreground">
                    {location.availableModemCount}/{location.modemCount}
                  </div>
                  <div className="text-xs text-muted-foreground">online</div>
                </div>

                <div className="p-3 rounded-lg bg-muted">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Activity className="w-4 h-4" />
                    <span className="text-xs">Tests (24h)</span>
                  </div>
                  <div className="text-2xl font-semibold text-foreground">
                    {recentExecutions.length}
                  </div>
                  <div className="text-xs text-muted-foreground">executions</div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="flex gap-2">
                <Button
                  variant="default"
                  className="flex-1"
                  onClick={() => navigate(`/flows?locationId=${location.id}`)}
                >
                  <PlayCircle className="w-4 h-4 mr-2" />
                  Run Test
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/edges')}
                >
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </div>

              {/* Recent Executions */}
              <div>
                <h4 className="text-sm font-medium text-foreground mb-3">
                  Recent Tests
                </h4>

                {recentExecutions.length === 0 ? (
                  <div className="text-sm text-muted-foreground text-center py-4 bg-muted/50 rounded-lg">
                    No recent tests at this location
                  </div>
                ) : (
                  <div className="space-y-2">
                    {recentExecutions.map((execution) => {
                      const config = EXECUTION_STATUS_CONFIG[execution.status];
                      const Icon = config.icon;

                      return (
                        <div
                          key={execution.id}
                          onClick={() => navigate(`/executions/${execution.id}`)}
                          className="flex items-center gap-3 p-2 rounded-lg border border-border hover:bg-muted/50 cursor-pointer transition-colors"
                        >
                          <Icon
                            className={`w-4 h-4 ${config.color} ${config.animate ? 'animate-spin' : ''}`}
                          />
                          <div className="flex-1 min-w-0">
                            <span className="text-sm text-foreground truncate block">
                              {execution.flowName}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {formatDistanceToNow(execution.startedAt, { addSuffix: true })}
                            </span>
                          </div>
                          {execution.duration && (
                            <span className="text-xs font-mono text-muted-foreground">
                              {execution.duration}s
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* View Full Details */}
              <div className="pt-4 border-t border-border">
                <button
                  onClick={() => navigate('/edges')}
                  className="w-full text-center text-sm text-accent-blue hover:text-accent-blue/80 transition-colors flex items-center justify-center gap-1"
                >
                  View full location details
                  <ExternalLink className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default LocationDetailPanel;
